import { defineStore } from 'pinia'
import { localStorage, ExpirableStorage } from '../../utils/storage/index'

import {
  getConfigStoreCacheKey,
  getConfigStorePaths,
} from '../../utils/getConfigStorePaths'

import debug from '../../utils/console'
const logger = debug.withTag('store:ConfigStore')

export interface ConfigStore<T> {
  data: Ref<T>
  isLoading: Ref<boolean>
  load: () => Promise<T>
  update: (data: Partial<T>) => void
  reset: () => void
}

export interface ConfigStoreOptions<T> {
  defaults: T
  configStoreKey: string
  schema: (data: unknown) => T
  onLoad?: (data: T) => void
  onUpdate?: (data: T) => void
  onReset?: () => void
  transform?: (data: T) => T
}

export function useConfigStore<T>(props: ConfigStoreOptions<T>) {
  const applySafeTransform = (data: T): T => {
    if (!props.transform) return data

    try {
      return props.transform(data)
    } catch (err) {
      logger.error(
        `[${props.configStoreKey}] Failed to apply transform function:`,
        err
      )
      return data
    }
  }

  return defineStore(props.configStoreKey, () => {
    const storageWrapper = new ExpirableStorage(
      localStorage,
      `app:config`,
      60 * 1000 // 1 minute
    )

    const data = ref<T>(props.defaults)
    const isLoading = ref(false)

    // Resolved in load() once the variant flag has been evaluated. Until then the
    // un-suffixed key is used, which is also the key control resolves to.
    let cacheKey = props.configStoreKey

    const fetchConfig = async (urls: string[]) => {
      const errors: string[] = []
      for (const url of urls) {
        try {
          const response = await fetch(url)
          if (!response.ok) {
            throw new Error(`Failed to fetch ${url}: ${response.statusText}`)
          }
          const json = await response.json()
          const validated = props.schema(json)
          return validated
        } catch (error) {
          logger.error(
            `[${props.configStoreKey}] Failed to load config from ${url}:`,
            error
          )
          errors.push(`Failed to fetch ${url}: ${error}`)
        }
      }
      throw new Error(`Failed to fetch config: ${errors.join(', ')}`)
    }

    const readCache = (): T | null => {
      const stored = storageWrapper.load(cacheKey)
      if (!stored) {
        return null
      }
      try {
        return props.schema(JSON.parse(stored))
      } catch (err) {
        logger.error(
          `[${props.configStoreKey}] Failed to parse or validate stored data:`,
          err
        )
        // Drop the bad entry and fall through to the network rather than pinning
        // the store to its defaults for the rest of the cache window.
        storageWrapper.remove(cacheKey)
        return null
      }
    }

    let loadPromise: Promise<T> | null = null
    const load = async (): Promise<T> => {
      if (loadPromise) {
        return loadPromise
      }

      isLoading.value = true

      // Evaluate the variant flag *before* touching the cache: a cache keyed only by
      // configStoreKey would otherwise serve control to a session that resolved a
      // variant (and keep serving a variant after the override went away).
      // Failing to resolve must degrade to the defaults, not reject `initialized`.
      let variant = ''
      let cacheable = false
      let urls: string[] = []
      try {
        const resolved = await getConfigStorePaths(props.configStoreKey)
        variant = resolved.variant
        cacheable = resolved.cacheable
        urls = resolved.urls
      } catch (err) {
        logger.error(
          `[${props.configStoreKey}] Failed to resolve config paths:`,
          err
        )
      }
      cacheKey = getConfigStoreCacheKey(props.configStoreKey, variant)

      const cached = readCache()

      if (cached) {
        const transformed = applySafeTransform(cached)
        data.value = transformed
        props.onLoad?.(transformed)
      } else {
        try {
          const validated = await fetchConfig(urls)
          const transformed = applySafeTransform(validated)
          data.value = transformed
          if (cacheable) {
            storageWrapper.save(cacheKey, JSON.stringify(transformed))
          }
          props.onLoad?.(transformed)
        } catch (err) {
          logger.error(
            `[${props.configStoreKey}] Failed to fetch or validate config:`,
            err
          )
        }
        if (!data.value) {
          logger.warn(
            `[${props.configStoreKey}] No valid data found in local storage or API`
          )
        }
      }
      isLoading.value = false
      return await Promise.resolve(data.value)
    }
    // Load the config when the store is created
    loadPromise = load()

    const update = (newData: Partial<T>) => {
      logger.log('updating config store', newData)
      const merged = { ...data.value, ...newData }
      data.value = applySafeTransform(merged)
      storageWrapper.save(cacheKey, JSON.stringify(data.value))
      props.onUpdate?.(data.value)
    }

    const reset = () => {
      const clone = structuredClone(props.defaults)
      data.value = applySafeTransform(clone)
      storageWrapper.save(cacheKey, JSON.stringify(data.value))
      props.onReset?.()
      loadPromise = null
    }

    return {
      data,
      isLoading,
      load,
      update,
      reset,
      storage: storageWrapper,
      initialized: loadPromise,
    }
  })
}
